import React from 'react';

interface WListProps {
  itemHeight: number;
  data: any[];
  itemComponent: React.FC<{ data: any; props?: any }>;
  maxHeight: number | 'auto';
  scrollConfig?: {
    fromBottom: number;
    event: () => Promise<void>;
  };
  props?: { [key: string]: any };
}

export const WList = ({
  maxHeight,
  itemHeight,
  data = [],
  scrollConfig,
  itemComponent,
  props,
}: WListProps) => {
  const ChildComponent = itemComponent;

  const wListRef = React.useRef<HTMLDivElement | null>(null);

  const [eventsStarted, setEventsStarted] = React.useState<boolean>(false);
  const [y, setY] = React.useState<number>(0);
  const [renderLimits, setRenderLimits] = React.useState<{
    start: number;
    end: number;
    height: number;
  }>({
    start: 0,
    end: 0,
    height: 0,
  });
  const [scrollTrigger, setScrollTrigger] = React.useState<number>(0);

  const renderAdjust = () => {
    if (maxHeight === 'auto') return;

    const start = Math.min(
      Math.max(data.length - Math.round(maxHeight / itemHeight), 0),
      Math.round(y / itemHeight),
    );
    const end = Math.min(Math.round(start + maxHeight / itemHeight), data.length);

    setRenderLimits({
      start: start,
      end: end,
      height: maxHeight,
    });
  };

  const scrollHandler = async () => {
    if (!wListRef.current) return;
    const { scrollHeight, scrollTop, clientHeight } = wListRef.current;
    const scroll = scrollHeight - scrollTop - clientHeight;
    setY(scrollTop);

    if (scrollConfig) {
      if (scroll - scrollConfig?.fromBottom <= 0) {
        setScrollTrigger(Math.random() * 1000000);
      }
    }
  };

  /* Start/End scroll listener */
  React.useEffect(() => {
    if (eventsStarted || !wListRef.current) return;
    setEventsStarted(true);

    wListRef.current?.addEventListener('scroll', scrollHandler);
    wListRef.current.scroll(0, 1);
    return () => {};
  }, [wListRef.current]);

  React.useEffect(() => {
    renderAdjust();
  }, [y, data]);
  React.useEffect(() => {
    if (scrollConfig?.event) scrollConfig?.event();
  }, [data, scrollTrigger]);

  return (
    <div
      ref={wListRef}
      style={{
        overflowY: 'auto',
        height: `${maxHeight}px`,
        boxSizing: 'border-box',
      }}
    >
      {/* Virtual Space From Top */}
      <div style={{ paddingTop: `${itemHeight * Math.min(renderLimits.start, data.length)}px` }}>
        {data
          .slice(renderLimits.start, Math.min(renderLimits.end, data.length))
          .map((item, index) => (
            <ChildComponent key={`wlistchild-${index}`} data={item} props={props} />
          ))}
      </div>
      {/* Virtual Space from Bottom */}
      <div style={{ paddingBottom: `${itemHeight * (data.length - renderLimits.end)}px` }} />
    </div>
  );
};
